import { useEffect } from "react";
import useErrorHandler from "../FormErrorHandler";
import {
  TextInput,
  FormInputRow,
  FormSection,
  FormHeader,
  SelectInput,
  DateInput,
  PhoneNumberInput,
  AddressInput,
  RadioInput,
  TextField,
} from "../../FormInput/FormInput";
import { validateZipcode } from "../Validations";

const GettingStarted = ({ formData, handleInputChange, validateForm }) => {
  const { error, addError, clearError, hasError } = useErrorHandler();

  const validate = async (formData) => {
    clearError();

    if (formData.involvedDriverIndex == "other") {
      if (!formData.cureDriverInfo.FirstName) {
        addError({ FirstName: "First name is required" });
      }

      if (!formData.cureDriverInfo.LastName) {
        addError({ LastName: "Last name is required" });
      }
    }

    if (!formData.cureDriverInfo.relationToPolicyHolder) {
      addError({
        relationToPolicyHolder: "Relationship to insured is required",
      });
    }

    if (!formData.cureDriverInfo.DLNumber) {
      addError({ DLNumber: "Driver's License Number is required" });
    }

    if (!formData.cureDriverInfo.DLState) {
      addError({ DLState: "Driver's License State is required" });
    }

    if (!formData.cureDriverInfo.PhoneNumber) {
      addError({ PhoneNumber: "Phone number is required" });
    }
    else if(formData.cureDriverInfo.PhoneNumber.length != 10){
      addError({ PhoneNumber: "Phone number must be 10 digits" });
    }

    if (!formData.cureDriverInfo.EmailAddress) {
      addError({ EmailAddress: "Email is required" });
    }

    if (!formData.cureDriverInfo.StreetAddress) {
      addError({ StreetAddress: "Address is required" });
    }

    if (!formData.cureDriverInfo.City) {
      addError({ City: "City is required" });
    }

    if (!formData.cureDriverInfo.ZipCode) {
      addError({ ZipCode: "Zipcode is required" });
    } else{
      if (isNaN(parseInt(formData.cureDriverInfo.ZipCode)) || formData.cureDriverInfo.ZipCode.toString().length != 5)
        addError({ ZipCode: "Enter a 5 digit zip code" });
      else {
        const state = await validateZipcode(formData.cureDriverInfo.ZipCode)
        if(!state)
          addError({ ZipCode: "Enter a valid zip code" });
        else
          handleInputChange("State", state, "cureDriverInfo");
      } 
    }

    if (!formData.cureDriverInfo.Injured) {
      addError({ Injured: "This field is required" });
    }
    else if (formData.cureDriverInfo.Injured == "Yes" && !formData.cureDriverInfo.InjuryDescription) {
      addError({ InjuryDescription: "This field is required" });
    }


    return !hasError();
  };

  validateForm.current = validate;

  const involvedDriverIndex = formData.involvedDriverIndex;
  const noDriverSelected =
    !involvedDriverIndex || involvedDriverIndex == "other";
  const involvedDriverInfo =
    !noDriverSelected && formData.drivers[involvedDriverIndex];

  const involvedDriverInfoDisplay = (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
      }}
    >
      <span>Driver:</span>
      <span>
        &nbsp;{involvedDriverInfo.fullName} - DOB ({involvedDriverInfo.DOB})
      </span>
    </div>
  );

  return (
    <div>
      <FormHeader>DRIVER INFORMATION</FormHeader>
      <FormSection>
        <FormInputRow
          header={
            noDriverSelected ? "Driver Full Name*" : involvedDriverInfoDisplay
          }
          textColor={"blue"}
        >
          {noDriverSelected && (
            <>
              <TextInput
                fieldName="FirstName"
                value={formData.cureDriverInfo.FirstName}
                handleInputChange={handleInputChange}
                placeholder="First Name"
                error={error}
                parentField="cureDriverInfo"
              />
              <TextInput
                fieldName="LastName"
                value={formData.cureDriverInfo.LastName}
                handleInputChange={handleInputChange}
                placeholder="Last Name"
                error={error}
                parentField="cureDriverInfo"
              />
            </>
          )}
        </FormInputRow>

        {involvedDriverIndex == "other" && (
          <FormInputRow header="Driver Date of Birth" sameLine={true}>
            <DateInput
              value={formData.cureDriverInfo.DOB}
              handleInputChange={handleInputChange}
              fieldName={"DOB"}
              props={{
                placeholderText: "MM/DD/YYYY",
                maxDate: new Date(),
                dateFormat: "MM/dd/yyyy"
              }}
              parentField="cureDriverInfo"
              hideKeyboardOnMobile={false}
              error={error}
              addError={addError}
            />
          </FormInputRow>
        )}

        <FormInputRow header="Relationship to the Insured*" sameLine={true}>
          <TextInput
            fieldName="relationToPolicyHolder"
            value={formData.cureDriverInfo.relationToPolicyHolder}
            handleInputChange={handleInputChange}
            error={error}
            parentField="cureDriverInfo"
          />
        </FormInputRow>

        <FormInputRow header="Driver's License Number*" sameLine={true}>
          <TextInput
            fieldName="DLNumber"
            value={formData.cureDriverInfo.DLNumber}
            handleInputChange={handleInputChange}
            error={error}
            parentField="cureDriverInfo"
          />
        </FormInputRow>

        <FormInputRow header="Driver's License State*" sameLine={true}>
          <SelectInput
            fieldName="DLState"
            value={formData.cureDriverInfo.DLState}
            handleInputChange={handleInputChange}
            error={error}
            parentField="cureDriverInfo"
            options={states}
            defaultText="Select a State"
          />
        </FormInputRow>

        <FormInputRow header="Phone Number*" sameLine={true}>
          <PhoneNumberInput
            fieldName="PhoneNumber"
            value={formData.cureDriverInfo.PhoneNumber}
            handleInputChange={handleInputChange}
            error={error}
            parentField="cureDriverInfo"
          />
        </FormInputRow>

        <FormInputRow header="Email*" sameLine={true}>
          <TextInput
            fieldName="EmailAddress"
            value={formData.cureDriverInfo.EmailAddress}
            handleInputChange={handleInputChange}
            error={error}
            parentField="cureDriverInfo"
          />
        </FormInputRow>

        <FormInputRow header="Home Address*">
          <AddressInput
            addressData={formData.cureDriverInfo}
            handleInputChange={handleInputChange}
            parentField="cureDriverInfo"
            error={error}
          />
        </FormInputRow>

        <FormInputRow header="Was this driver injured in the loss?*">
          <RadioInput
            fieldName="Injured"
            value={formData.cureDriverInfo.Injured}
            handleInputChange={(fieldname, value, parent) => 
              {
                handleInputChange(fieldname, value, parent)
                if(value == "No")
                  handleInputChange("InjuryDescription", null, parent)
              }
            }
            parentField="cureDriverInfo"
            sameLine={true}
            error={error}
            options={[
              { label: "Yes", value: "Yes" },
              { label: "No", value: "No" },
            ]}
          />
        </FormInputRow>
        
        {formData.cureDriverInfo.Injured == "Yes" &&
          <FormInputRow header="Please describe any injuries this driver sustained in the loss:*">
            <TextField
              fieldName="InjuryDescription"
              value={formData.cureDriverInfo.InjuryDescription}
              handleInputChange={handleInputChange}
              parentField="cureDriverInfo"
              error={error}
            />
          </FormInputRow>
        }
      </FormSection>
    </div>
  );
};

const states = [
  { label: "Alabama", value: "AL" },
  { label: "Alaska", value: "AK" },
  { label: "Arizona", value: "AZ" },
  { label: "Arkansas", value: "AR" },
  { label: "California", value: "CA" },
  { label: "Colorado", value: "CO" },
  { label: "Connecticut", value: "CT" },
  { label: "Delaware", value: "DE" },
  { label: "Florida", value: "FL" },
  { label: "Georgia", value: "GA" },
  { label: "Hawaii", value: "HI" },
  { label: "Idaho", value: "ID" },
  { label: "Illinois", value: "IL" },
  { label: "Indiana", value: "IN" },
  { label: "Iowa", value: "IA" },
  { label: "Kansas", value: "KS" },
  { label: "Kentucky", value: "KY" },
  { label: "Louisiana", value: "LA" },
  { label: "Maine", value: "ME" },
  { label: "Maryland", value: "MD" },
  { label: "Massachusetts", value: "MA" },
  { label: "Michigan", value: "MI" },
  { label: "Minnesota", value: "MN" },
  { label: "Mississippi", value: "MS" },
  { label: "Missouri", value: "MO" },
  { label: "Montana", value: "MT" },
  { label: "Nebraska", value: "NE" },
  { label: "Nevada", value: "NV" },
  { label: "New Hampshire", value: "NH" },
  { label: "New Jersey", value: "NJ" },
  { label: "New Mexico", value: "NM" },
  { label: "New York", value: "NY" },
  { label: "North Carolina", value: "NC" },
  { label: "North Dakota", value: "ND" },
  { label: "Ohio", value: "OH" },
  { label: "Oklahoma", value: "OK" },
  { label: "Oregon", value: "OR" },
  { label: "Pennsylvania", value: "PA" },
  { label: "Rhode Island", value: "RI" },
  { label: "South Carolina", value: "SC" },
  { label: "South Dakota", value: "SD" },
  { label: "Tennessee", value: "TN" },
  { label: "Texas", value: "TX" },
  { label: "Utah", value: "UT" },
  { label: "Vermont", value: "VT" },
  { label: "Virginia", value: "VA" },
  { label: "Washington", value: "WA" },
  { label: "West Virginia", value: "WV" },
  { label: "Wisconsin", value: "WI" },
  { label: "Wyoming", value: "WY" },
];

export default GettingStarted;
